import { useEffect, useState } from 'react';

/**
 * The agent's colour, resolved from the page rather than hardcoded.
 *
 * Resolution order:
 *   1. `--agent-accent` — a page that wants to drive the agent sets this. The
 *      home corridor writes it per chamber, so the agent shifts hue as the
 *      camera travels.
 *   2. `--persona-primary` — set by the persona theming system, so a visitor
 *      who generates their own theme gets an agent that matches it.
 *   3. `--bs-accent-fill` — the shared avatar fill, readable with dark eyes.
 *   4. `--bs-accent` — compatibility with palettes defining only a text accent.
 *   5. `--bs-signal` — compatibility with palettes defining only a signal.
 *   6. the supplied fallback.
 *
 * Watched with a MutationObserver on the root element's `style` and `class`,
 * which is how both of those systems apply changes. No polling.
 */
export function useAgentAccent(fallback = '#7dd3fc'): string {
    const [accent, setAccent] = useState(fallback);

    useEffect(() => {
        const root = document.documentElement;

        const read = () => {
            const cs = getComputedStyle(root);
            const explicit = cs.getPropertyValue('--agent-accent').trim();
            const persona = cs.getPropertyValue('--persona-primary').trim();
            const fill = cs.getPropertyValue('--bs-accent-fill').trim();
            const brand = cs.getPropertyValue('--bs-accent').trim();
            const signal = cs.getPropertyValue('--bs-signal').trim();
            const next = explicit || persona || fill || brand || signal || fallback;
            setAccent(prev => (prev === next ? prev : next));
        };

        read();
        const mo = new MutationObserver(read);
        mo.observe(root, { attributes: true, attributeFilter: ['style', 'class', 'data-theme'] });

        // The theme toggle swaps a class on <body> in some paths; watch it too.
        const moBody = new MutationObserver(read);
        moBody.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });

        return () => { mo.disconnect(); moBody.disconnect(); };
    }, [fallback]);

    return accent;
}

/**
 * Drive the agent's colour from a page. Pass null to hand control back to the
 * persona theme.
 */
export function setAgentAccent(color: string | null): void {
    const root = document.documentElement;
    if (color) root.style.setProperty('--agent-accent', color);
    else root.style.removeProperty('--agent-accent');
}
